import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {CustomerService} from '../../services/customer.service';
import {SharedService} from '../../services/shared.service';
import {Subject} from 'rxjs';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EMAIL_PATTERN, markFormGroupTouched, PHONE_MASK, PHONE_PATTERN} from '../../global';
import {ActivatedRoute, ActivationEnd, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntil} from 'rxjs/operators';
import {ForgotPasswordComponent} from '../../components/forgot-password/forgot-password.component';
import {MatDialog, MatDialogConfig} from '@angular/material';

@Component({
  selector: 'app-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class IndexComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  registerForm: FormGroup;
  signInForm: FormGroup;
  codeForm: FormGroup;
  form: string = 'register';
  phoneVerified: string; //Phone number where we sent code
  customerData: any;
  showLoading: boolean;
  phoneMask = PHONE_MASK;
  verificationCodeMask = [/\d/, /\d/, /\d/, /\d/];
  submittedForm: string;

  constructor(private customerService: CustomerService,
              private fb: FormBuilder,
              private shared: SharedService,
              private dialog: MatDialog,
              private router: Router,
              private route: ActivatedRoute,
              private zone: NgZone) {

  }

  ngOnInit() {
    this.registerForm = this.fb.group({
      displayName: [null, Validators.required],
      email: [null, [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      password: [null, [Validators.required, Validators.minLength(7)]],
      phone: [null, [Validators.required, Validators.pattern(PHONE_PATTERN)]]
    });
    this.signInForm = this.fb.group({
      phone: [null, Validators.pattern(PHONE_PATTERN)]
    });
    this.codeForm = this.fb.group({
      code: [null, [Validators.required, Validators.pattern('^[0-9]{4}')]]
    });
    this.shared.eventObservable
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(event => {
        if (event.type == 'sign_in') {
          this.form = 'signin';
        }
      });
    this.customerService.authChanged()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(customer => {
        if (customer && customer.email) return this.zone.run(() => this.router.navigate(['/account/request/fuel']));
      });
    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(params => {
        let action = params.action;
        if (action) {
          if (action == 'forgot') {
            setTimeout(() => {
              this.forgotPassword();
            }, 1000);
          } else {
            try {
              let data = JSON.parse(atob(params.data));
              if (action == 'signin') this.signInForm.patchValue(data);
              if (action == 'register') {
                this.registerForm.patchValue({
                  displayName: data.name,
                  email: data.email,
                  password: data.password,
                  phone: data.phone
                });
              }
              this.submittedForm = action;
              this.phoneVerified = data.phone;
              this.form = 'verification';
            } catch (e) {
              console.log(e);
            }
          }
        }
      });
  }

  sendSMSVerification(phone: string, from: string) {
    this.showLoading = true;
    this.codeForm.reset();
    this.submittedForm = from;
    this.customerService.sendSms('+1', phone)
      .then(res => {
        setTimeout(() => {
          this.showLoading = false;
          if (res.isSent) this.form = 'verification';
        }, 3000);
      })
      .catch((err: HttpErrorResponse) => {
        this.showLoading = false;
        let message = err.error.errorMessage || 'Server error. Please try again';
        this.shared.openErrorDialog({title: 'Error', body: message});
      });
  }

  verifySmsCode(form: FormGroup) {
    if (form.valid) {
      this.showLoading = true;
      this.customerService.smsCodeVerification('+1', this.phoneVerified, form.value.code)
        .then(res => {
          this.showLoading = false;
          if (res.token) return this.customerService.signInWithToken(res.token);
          else return Promise.reject({errorMessage: 'Processing data error. Please try again'});
        })
        .then(customer => {
          if (customer.email) { //If customer already have profile updated - navigate to account
            return Promise.resolve(true);
          } else {
            if (!this.customerData) {
              this.form = 'register';
              return Promise.reject({errorMessage: 'User doesn\'t exist. Please use registration form'});
            }
            if (this.customerData) return this.customerService.updateCustomerProfile(this.customerData);
          }
        })
        .then(_ => {
          return this.router.navigate(['account', 'request', 'fuel']);
        })
        .catch(err => {
          this.showLoading = false;
          let errorMessage = err.errorMessage || err.error.errorMessage || 'Server error. Please try again.';
          this.shared.openErrorDialog({title: 'Error', body: errorMessage});
        });
    } else {
      this.shared.openErrorDialog({title: 'Error', body: 'Invalid code'});
    }

  }

  submitRegisterForm(form: FormGroup) {
    markFormGroupTouched(form);
    this.customerData = null;
    this.phoneVerified = null;
    if (form.valid) {
      let data = form.value;
      data.phone = data.phone.match(/\d/g).join('');
      this.showLoading = true;
      this.customerService.getCustomerByPhone(data.phone)
        .then(customer => {
          if (customer && customer.email) {
            this.signInForm.patchValue({phone: data.phone});
            this.registerForm.reset();
            this.shared.openErrorDialog({title: 'Error', body: 'User already exists. Please sign in'}, 'sign_in');
            this.showLoading = false;
          } else {
            this.sendSMSVerification(data.phone, 'register');
            this.customerData = data;
            if (data.phone === "7033006967") this.customerData.giveaway = true;  //Change the number to the giveaway winner
            this.phoneVerified = data.phone;
          }
        });

    }
  }

  submitSignIn(form: FormGroup) {
    this.customerData = null;
    this.phoneVerified = null;
    markFormGroupTouched(form);
    if (form.valid) {
      let phone = form.value.phone.match(/\d/g).join('');
      this.phoneVerified = phone;
      this.sendSMSVerification(phone, 'signin');
    }
  }

  continueAsGuest() {
    this.router.navigate(['/account/request/fuel']);
  }

  ngOnDestroy(): void {
    //Unsubscribe from all events
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  forgotPassword() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    this.dialog.open(ForgotPasswordComponent, dialogConfig);
  }
}
